import { ALLOCATION_LABEL, ALLOCATION_POLICIES, fmt, type AllocationPolicy, type CalcResult, type Scenario } from "@h2lca/core";
import { Button, SelectField, TextField } from "@/components/ui/FormControls";
import { useWorkshop } from "@/store/workshop";
import { GlossaryTip } from "../GlossaryTip";
import { NumField } from "../NumField";
import { Block } from "../Section";

/** 부산물 배분 , 정책과 부산물 목록, 결과 배분계수 */
export function AllocationEditor({ scenario, result }: { scenario: Scenario; result: CalcResult }) {
  const update = useWorkshop((s) => s.update);
  const alloc = scenario.allocation;
  const setAlloc = (patch: Partial<Scenario["allocation"]>) => update((s) => ({ ...s, allocation: { ...s.allocation, ...patch } }));
  const setBp = (i: number, patch: Partial<Scenario["allocation"]["byproducts"][number]>) => setAlloc({ byproducts: alloc.byproducts.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  const energy = alloc.policy === "energy";

  return (
    <Block
      title={
        <>
          부산물 배분 <GlossaryTip k="allocation" />
        </>
      }
    >
      <SelectField label="배분 정책" value={alloc.policy} onChange={(e) => setAlloc({ policy: e.target.value as AllocationPolicy })} hint="ISO 14044 는 배분을 피하는 것을 우선합니다. 교육용 기본은 배분 없음(전량 수소 귀속)입니다.">
        {ALLOCATION_POLICIES.map((p) => (
          <option key={p} value={p}>
            {ALLOCATION_LABEL[p]}
          </option>
        ))}
      </SelectField>
      {alloc.policy !== "none" && (
        <div className="flex flex-col gap-item">
          <p className="flex items-center gap-label text-caption font-semibold text-ink">
            부산물 <GlossaryTip k="byproduct" />
          </p>
          {alloc.byproducts.map((b, i) => (
            <div key={i} className="ws-allocation-row">
              <TextField label="이름" value={b.name} onChange={(e) => setBp(i, { name: e.target.value })} />
              <NumField label="양" unit="kg" value={b.amountKg} allowNull={false} min={0} onChange={(v) => v !== null && setBp(i, { amountKg: v })} />
              {energy && <NumField label="저위발열량" unit="MJ/kg" value={b.lhvMJperKg} allowNull={false} min={0} onChange={(v) => v !== null && setBp(i, { lhvMJperKg: v })} hint="산소는 0" />}
              <span className="justify-self-start"><Button type="button" onClick={() => setAlloc({ byproducts: alloc.byproducts.filter((_, j) => j !== i) })}>
                제거
              </Button></span>
            </div>
          ))}
          <Button variant="secondary" onClick={() => setAlloc({ byproducts: [...alloc.byproducts, { name: "산소", amountKg: Math.round(result.batch.producedKg * 8), lhvMJperKg: 0 }] })}>
            부산물 추가
          </Button>
          {alloc.byproducts.length === 0 && <p className="text-footnote text-ink-subtle">부산물이 없으면 배분계수는 1(전량 수소 귀속)입니다. 수전해는 수소 1 kg 당 산소 약 8 kg 이 나옵니다.</p>}
        </div>
      )}
      <dl className="ws-kv border-t border-line-soft pt-item">
        <dt>배분계수</dt>
        <dd>
          <b>{fmt(result.allocation.factor, 4)}</b>
        </dd>
        <dt>적용</dt>
        <dd className="font-sans text-ink-muted">{result.allocation.note}</dd>
      </dl>
      <p className="text-footnote text-ink-subtle">배분은 생산시설(원료 조달과 내부) 배출에만 적용하고, 출하 이후 설비 배출은 전량 수소에 귀속합니다.</p>
    </Block>
  );
}
